import Axios from 'axios'
import { v4 } from 'uuid';
import { QueryClient } from '@tanstack/react-query';
const MAX_ATTEMPTS = 3;
const BACKOFF = 1000;
import { defaultTanstackConfig } from "@/config/network";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            ...defaultTanstackConfig.queries
        },
    },
});

export const baseUrl = () => {
    // if (typeof process !== 'undefined' && process?.env?.FORMITIZE_ROOT && typeof process?.env?.FORMITIZE_ROOT === 'string' && process?.env?.FORMITIZE_ROOT.length > 0) {
    //     console.log('process.env.FORMITIZE_ROOT', process.env.FORMITIZE_ROOT);
    //     return process.env.FORMITIZE_ROOT;
    // }

    return window?.myNS?.config?.server ?? window.location.href;
}

export const generateUrl = (url = null) => {
    let base = baseUrl();

    if (!base || typeof base !== 'string' || (typeof base === 'string' && base.length === 0)) {
        base = window.location.href;
    }

    if (!url) return base;

    const baseUrlObject = new URL(base);

    if (url === '/') {
        return baseUrlObject.origin + '/';
    }

    if (url && url.startsWith('/')) {
        const relativeUrlObject = new URL(url, baseUrlObject.origin);

        return relativeUrlObject.href;
    }

    return url;
}

const axios = Axios.create({
    baseURL: baseUrl(),
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        "Content-Type": "application/x-www-form-urlencoded"
    }
});

const getConfig = (config) => {
    const headers = {...config?.headers ?? {}};

    // @TODO this is a placeholder for future functionality.
    const jwt = sessionStorage.getItem("authJwt");
    if(jwt) {
        headers['Authorization'] = `Bearer ${jwt}`;
    }

    return {
        ...config,
        headers,
    }
}

/**
 * Adds app related params to the request body
 *
 * @param data
 * @param config
 * @returns {*}
 */
export const prepare = (data, config = null) => {
    if (config?.headers?.Authorization) {
        return data;
    }

    return window?.formitize?.api?.network?.request?.header ? {...window.formitize.api.network.request.header(data), _mobileShim: true,} : data;
}

const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

const __axiosPerform = (method, url, data, config = {}, raw = false) => {
    const nonce = v4();
    let attempt = 0;
    const cmd = prepare(data, config);
    const finalUrl = generateUrl(url);

    const action = () => {
        return new Promise(
            (resolve, reject) => {
                axios[method](finalUrl, {
                    '__nonce': nonce, 
                    ...cmd
                }, getConfig(config)).then(resp => {
                    //For testing purposes - uncomment the line below to trigger an 'error'
                    //if(attempt < MAX_ATTEMPTS) throw "test";
                    
                    if(raw === true) {
                        resolve(resp);
                        return;
                    }
                    resolve(resp.data);
                }).catch(async (ex) => {
                    
                    //If you want to see this in action just uncomment the alert below
                    //alert(`${attempt} < ${MAX_ATTEMPTS} ${JSON.stringify(data)}`);
                    if(attempt < MAX_ATTEMPTS) {
                        attempt++;
                        await sleep(BACKOFF * attempt);
                        action().then(resolve).catch(reject);
                        return;
                    }
                    reject(ex);
                })
            }
        )
    }

    return action();
}

export const post = (url, data, config = {}, raw = false, ) => {
    return __axiosPerform('post', url, data, config, raw);
}

export const postForm = (url, data, config = {}, raw = false) => {
    return __axiosPerform('postForm', url, data, config, raw);
}

export const put = (url, data, config = {}, raw = false) => {
    return __axiosPerform('put', url, data, config, raw);
}

export const get = (url, data = {}, config = {}, raw = false) => {
    let attempt = 0;
    const finalUrl = generateUrl(url);

    const action = () => {
        return new Promise(
            (resolve, reject) => {
                axios.get(finalUrl, {
                    params: data,
                    ...getConfig(config)
                }).then(resp => {
                    //For testing purposes - uncomment the line below to trigger an 'error'
                    //if(attempt < MAX_ATTEMPTS) throw "test";
                    
                    if(raw === true) {
                        resolve(resp);
                        return;
                    }
                    resolve(resp.data);
                }).catch(async (ex) => {
                    
                    //If you want to see this in action just uncomment the alert below
                    //alert(`${attempt} < ${MAX_ATTEMPTS} ${JSON.stringify(data)}`);
                    if(attempt < MAX_ATTEMPTS) {
                        attempt++;
                        await sleep(BACKOFF * attempt);
                        action().then(resolve).catch(reject);
                        return;
                    }
                    reject(ex);
                })
            }
        )
    }

    return action();
}

export const postWithCache = (url, data, config = {}, raw = false, force = false) => {
    const normalizedConfig = Object.keys(config).length === 0 ? null : config;
    const queryKey = ['axios', 'post', url, data, normalizedConfig, raw];
    
    if (force) {
        queryClient.removeQueries({ queryKey });
    }
    
    return queryClient.fetchQuery({
        queryKey,
        queryFn: () => post(url, data, config, raw),
        ...defaultTanstackConfig.queries
    });
};

export const postFormWithCache = (url, data, config = {}, raw = false, force = false) => {
    const normalizedConfig = Object.keys(config).length === 0 ? null : config;
    const queryKey = ['axios', 'postForm', url, data, normalizedConfig, raw];
    
    if (force) {
        queryClient.removeQueries({ queryKey });
    }
    
    return queryClient.fetchQuery({
        queryKey,
        queryFn: () => postForm(url, data, config, raw),
        ...defaultTanstackConfig.queries
    });
};

export const getWithCache = (url, data = {}, config = {}, raw = false, force = false) => {
    const normalizedData = Object.keys(data).length === 0 ? null : data;
    const normalizedConfig = Object.keys(config).length === 0 ? null : config;
    const queryKey = ['axios', 'get', url, normalizedData, normalizedConfig, raw];
    
    if (force) {
        queryClient.removeQueries({ queryKey });
    }
    
    return queryClient.fetchQuery({
        queryKey,
        queryFn: () => get(url, data, config, raw),
        ...defaultTanstackConfig.queries
    });
};

export const invalidateQueries = (method, url) => {
    queryClient.invalidateQueries({ 
        queryKey: ['axios', method, url],
        exact: false
    });
};


export default axios
